import type { ReactNode } from 'react';
import { REPO } from '../config';
import type { Viewer } from '../github/access';
import type { InstallState } from '../pwa/install';
import type { RefusedReason } from '../workcopy/workspace';
import { AlertIcon, CheckIcon, FolderIcon, LockIcon } from './icons';

// ---------- First run (design/screens/05-first-run.html) ----------

type StepStatus = 'done' | 'active' | 'pending';

function Step({ n, status, title, children }: { n: number; status: StepStatus; title: string; children: ReactNode }) {
  return (
    <li className={`hq-step hq-step--${status}`} aria-current={status === 'active' ? 'step' : undefined}>
      <span className="hq-step__marker">
        {status === 'done' ? <CheckIcon /> : n}
        <span className="hq-visually-hidden">{status === 'done' ? ' (done)' : status === 'active' ? ' (current step)' : ' (not started)'}</span>
      </span>
      <div className="hq-step__body">
        <h2 className="hq-step__title">{title}</h2>
        {children}
      </div>
    </li>
  );
}

export type FolderStep = {
  refused?: { reason: RefusedReason; detail?: string } | undefined;
  onChoose: () => void;
  /** Set while downloading main into the chosen folder (step 4). */
  download?: { folderName: string; done: number; total: number } | undefined;
};

type SetupProps = {
  viewer: Viewer | null;
  sessionExpired?: boolean;
  install: InstallState;
  onSignIn: () => void;
  folder?: FolderStep;
};

const REFUSED: Record<RefusedReason, (detail?: string) => string> = {
  'not-empty': () => 'That folder isn’t empty. Choose an empty folder, or the folder you used for Hashquill before.',
  'other-repo': (d) => `That folder belongs to another repository (${d ?? 'unknown'}). Choose an empty folder.`,
  damaged: (d) => `That folder’s Hashquill data is damaged (${d ?? 'unreadable'}). Nothing was changed. Choose another folder.`,
  cancelled: () => 'No folder chosen. If Chrome said the folder contains system files, create a new folder, for example Documents/Specs, and choose that.',
};

export function Setup({ viewer, sessionExpired = false, install, onSignIn, folder }: SetupProps) {
  const signedIn = viewer !== null;
  const installed = install.kind === 'standalone';
  const installStatus: StepStatus = !signedIn ? 'pending' : installed ? 'done' : 'active';
  const downloading = folder?.download;
  const folderStatus: StepStatus = downloading ? 'done' : signedIn && installed ? 'active' : 'pending';
  const downloadStatus: StepStatus = downloading ? 'active' : 'pending';

  return (
    <main className="hq-page">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <h1 className="hq-title">Set up Hashquill</h1>
        <p className="hq-lead">Once per computer. Works in Chrome and Edge, on Mac and Windows.</p>
      </div>
      <ol className="hq-steps">
        <Step n={1} status={signedIn ? 'done' : 'active'} title="Sign in with GitHub">
          {signedIn ? (
            <p className="hq-lead">Signed in as <strong>{viewer.login}</strong>. Access to <code>{REPO.full}</code> confirmed.</p>
          ) : (
            <>
              {sessionExpired && (
                <p className="hq-lead" role="status">Your session expired. Sign in again; your local work is safe in your folder.</p>
              )}
              <p className="hq-lead">Hashquill uses your GitHub account to read and update <code>{REPO.full}</code>.</p>
              <div><button type="button" className="hq-btn" onClick={onSignIn}>Sign in with GitHub</button></div>
            </>
          )}
        </Step>

        <Step n={2} status={installStatus} title="Install the app">
          <p className="hq-lead">Hashquill opens in its own window and keeps your folder connected.</p>
          {installStatus === 'active' && <InstallAction install={install} />}
        </Step>

        <Step n={3} status={folderStatus} title="Choose your specs folder">
          <p className="hq-lead">Your copy of the specs lives in this folder. Create an empty one first, for example <code>Documents/Specs</code>.</p>
          {folderStatus === 'active' && (
            <>
              <ul>
                <li>Not your home folder.</li>
                <li>Not a synced folder: iCloud Drive, Dropbox, OneDrive or Google Drive.</li>
                <li>On a disk with encryption turned on (FileVault on Mac, BitLocker on Windows).</li>
              </ul>
              {folder?.refused && <p className="hq-lead hq-error" role="alert">{REFUSED[folder.refused.reason](folder.refused.detail)}</p>}
              <div><button type="button" className="hq-btn" onClick={folder?.onChoose} disabled={!folder}><FolderIcon />Choose folder</button></div>
            </>
          )}
          {downloading && <p className="hq-lead">Using <strong>{downloading.folderName}</strong>.</p>}
        </Step>

        <Step n={4} status={downloadStatus} title="Download the specs">
          <p className="hq-lead">Copies <code>main</code> into your folder.</p>
          {downloading && (
            <>
              <progress className="hq-progress" max={downloading.total || 1} value={downloading.done}
                aria-label="Downloading the specs" />
              <p className="hq-note" role="status">
                {downloading.total ? `Downloaded ${downloading.done} of ${downloading.total} files` : 'Looking up the files on GitHub…'}
              </p>
              <span className="hq-git">git clone https://github.com/{REPO.full}.git</span>
            </>
          )}
        </Step>
      </ol>
    </main>
  );
}

function InstallAction({ install }: { install: InstallState }) {
  switch (install.kind) {
    case 'promptable':
      return <div><button type="button" className="hq-btn" onClick={() => void install.prompt()}>Install Hashquill</button></div>;
    case 'installed-elsewhere':
      return <p className="hq-lead" role="status">Installed. Continue in the Hashquill app window; you can close this tab.</p>;
    case 'manual':
      return (
        <p className="hq-note">
          Use the install icon at the right of the address bar, or the browser menu: Cast, save and share → Install page as app.
          If Hashquill is already installed, open it from your Dock, Start menu or Launchpad.
        </p>
      );
    case 'standalone':
      return null;
  }
}

// ---------- No access (design/screens/06-no-access.html) ----------

export function NoAccess({ viewer, reason, onRetry, onSwitchAccount }: {
  viewer: Viewer; reason: 'not-found' | 'read-only'; onRetry: () => void; onSwitchAccount: () => void;
}) {
  return (
    <main className="hq-page hq-page--narrow">
      <span className="hq-icon-accent"><LockIcon /></span>
      <h1 className="hq-title hq-title--small">You don’t have access to this repo</h1>
      {reason === 'not-found' ? (
        <p className="hq-lead">You’re signed in as <strong>{viewer.login}</strong>, who can’t open <code>{REPO.full}</code>. Ask a repository admin to add you, then try again.</p>
      ) : (
        <p className="hq-lead">You’re signed in as <strong>{viewer.login}</strong>, who can read <code>{REPO.full}</code> but can’t push to it. Ask a repository admin for write access, then try again.</p>
      )}
      <div className="hq-btn-row">
        <button type="button" className="hq-btn" onClick={onRetry}>Try again</button>
        <button type="button" className="hq-btn hq-btn--secondary" onClick={onSwitchAccount}>Use another GitHub account</button>
      </div>
    </main>
  );
}

// ---------- Unsupported browser (SPEC §10) ----------

export function Unsupported() {
  return (
    <main className="hq-page hq-page--narrow">
      <span className="hq-icon-accent"><AlertIcon /></span>
      <h1 className="hq-title hq-title--small">Please use Chrome or Edge</h1>
      <p className="hq-lead">Hashquill keeps your copy of the specs in a folder on your computer. Only Chrome and Edge let web apps do that, on Mac and Windows.</p>
    </main>
  );
}

// ---------- Errors and loading ----------

export function ErrorScreen({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <main className="hq-page hq-page--narrow">
      <span className="hq-icon-accent"><AlertIcon /></span>
      <h1 className="hq-title hq-title--small">Something went wrong</h1>
      <p className="hq-lead hq-error">{message}</p>
      <div><button type="button" className="hq-btn" onClick={onRetry}>Try again</button></div>
    </main>
  );
}

export function Loading() {
  return (
    <main className="hq-page" aria-busy="true" aria-label="Loading">
      <div className="hq-skeleton" style={{ width: '60%', height: 36 }} />
      <div className="hq-skeleton" style={{ width: '90%' }} />
      <div className="hq-skeleton" style={{ width: '75%' }} />
    </main>
  );
}

export function VersionBanner({ onReload }: { onReload: () => void }) {
  return (
    <div className="hq-banner" role="status">
      New version available
      <span aria-hidden="true">·</span>
      <button type="button" onClick={onReload}>Reload</button>
    </div>
  );
}
